import React, { useState, useMemo } from 'react';
import { ChevronDown, Plus, Trash2, Camera, Check, Star, Users, Heart } from 'lucide-react';

const PHOTO_GROUPS = [
  {
    id: 'prep', label: 'Preparations', items: [
      'Wedding dressn/antrekket hengende', 'Shoes and accessories', 'Jewelry og detaljer',
      'Boutonni\u00e8re/korsasje', 'Getting ready \u2013 Person 1', 'Getting ready \u2013 Person 2',
      'Hair and makeup', 'Reaksjon ved f\u00f8rste blikk p\u00e5 antrekket',
      'First look (privat)', 'G\u00e5 ut av d\u00f8ren', 'Arrival til lokalet',
    ],
  },
  {
    id: 'ceremony', label: 'Ceremony', items: [
      'Venuet f\u00f8r the guests ankommer', 'Guests arriving',
      'Procession \u2013 weddingf\u00f8lget', 'Procession \u2013 Person 1/Person 2',
      'Ceremonyl\u00f8ftene', 'Ringveksling', 'F\u00f8rste kyss',
      'Leaving the ceremony', 'Congratulations from guests',
    ],
  },
  {
    id: 'portraits', label: 'Portretter', items: [
      'Paret alene \u2013 klassisk', 'Paret alene \u2013 kreativt',
      'Paret med weddingfollowt', 'Person 1 med forlovere',
      'Person 2 med forlovere', 'Paret med foreldre',
      'Paret med besteforeldre', 'S\u00f8sken',
    ],
  },
  {
    id: 'group', label: 'Group photos', items: [
      'All guests', 'Family Person 1', 'Family Person 2',
      'N\u00e6re venner', 'Kollegaer', 'Childdomsvenner',
      'Brideens side', 'Groommens side',
    ],
  },
  {
    id: 'reception', label: 'Fest & Middag', items: [
      'Borddekkingen', 'Bordseatsing', 'Blomster og dekor',
      'Welcome speech', 'F\u00f8rste dans', 'Speeches',
      'Kakekutting', 'Kaste bukett', 'Festen i gang',
      'DJ / band', 'Dansegulvet', 'Sparklers / avslutning',
    ],
  },
  {
    id: 'details', label: 'Detaljer & Dekor', items: [
      'Invitasjon/brevpapir', 'Ringer', 'Buketten',
      'Place cards/tablenummer', 'Menu cards', 'Kaken',
      'Desserttableet', 'Gavetable', 'Guest book',
      'Fotoboks-detaljer', 'Bar-oppsett',
    ],
  },
  {
    id: 'memorial', label: 'Memorial moment', items: [
      'Minnestol / reservert plass', 'Minnetable med bilder',
      'Minnesmykke / charm p\u00e5 bukett', 'Light for de vi savner',
    ],
  },
];

const PRIORITIES = ['Must have', 'Nice to have'];

const emptyPhoto = (label) => ({
  label, checked: true, people: [], location: '', time: '', priority: 'Must have',
  reference: '', notes: '',
});

export const PhotoChecklist = ({ photos, onChange, guests, couple, memorials }) => {
  const [openGroups, setOpenGroups] = useState(['prep', 'ceremony']);
  const [customPhotos, setCustomPhotosState] = useState([]);
  const data = photos || {};

  const p1 = couple?.person1Name || 'Person 1';
  const p2 = couple?.person2Name || 'Person 2';

  const toggle = (id) => setOpenGroups(p => p.includes(id) ? p.filter(s => s !== id) : [...p, id]);

  const getItem = (groupId, label) => data[`${groupId}__${label}`] || emptyPhoto(label);

  const updateItem = (groupId, label, updates) => {
    const key = `${groupId}__${label}`;
    onChange({ ...data, [key]: { ...(data[key] || emptyPhoto(label)), ...updates } });
  };

  // Substitute couple names in photo labels
  const sub = (label) => label.replace(/Person 1/g, p1).replace(/Person 2/g, p2);

  // Guest names for person picker
  const guestNames = useMemo(() =>
    (guests || []).filter(g => g.name?.trim()).map(g => g.name),
    [guests]
  );

  // Memorial items exist?
  const hasMemorials = (memorials || []).some(m => m.name?.trim());

  // Stats
  const stats = useMemo(() => {
    let total = 0, checked = 0, must = 0;
    PHOTO_GROUPS.forEach(g => g.items.forEach(label => {
      total++;
      const item = getItem(g.id, label);
      if (item.checked) { checked++; if (item.priority === 'Must have') must++; }
    }));
    const customChecked = (data._custom || []).filter(c => c.checked).length;
    return { total: total + (data._custom || []).length, checked: checked + customChecked, must };
  }, [data]);

  // Custom photos
  const addCustom = () => {
    const customs = data._custom || [];
    onChange({ ...data, _custom: [...customs, { ...emptyPhoto('Egendefinert bilde'), label: '' }] });
  };

  const updateCustom = (i, updates) => {
    const customs = [...(data._custom || [])];
    customs[i] = { ...customs[i], ...updates };
    onChange({ ...data, _custom: customs });
  };

  const removeCustom = (i) => {
    const customs = (data._custom || []).filter((_, j) => j !== i);
    onChange({ ...data, _custom: customs });
  };

  return (
    <div data-testid="photo-checklist" className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-sans font-semibold text-stone-800 text-sm">Fotoliste</h3>
          <p className="text-xs font-sans text-stone-500 mt-0.5">Checkliste over bilder for fotografen</p>
        </div>
        <div className="text-[10px] font-sans text-stone-500">
          <span className="font-bold text-primary">{stats.checked}</span> / {stats.total} selected
          <span className="text-stone-400 ml-2">({stats.must} must-have)</span>
        </div>
      </div>

      {/* Memorial suggestion */}
      {hasMemorials && (
        <div className="bg-rose-50/50 rounded-xl border border-rose-200/50 p-3 flex items-start gap-2" data-testid="memorial-photo-suggestion">
          <Heart className="w-3.5 h-3.5 text-rose-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-[10px] font-sans font-bold text-stone-600 mb-0.5">Minnebilder</p>
            <p className="text-[10px] font-sans text-stone-500">
              Du has lagt inn people under &laquo;De vi b&aelig;rer med oss&raquo;. Husk &aring; ta bilder av minnestol, smykke, lys or bukett-charm.
            </p>
          </div>
        </div>
      )}

      {/* Photo groups */}
      <div className="space-y-2" data-testid="photo-groups">
        {PHOTO_GROUPS.map(group => {
          const isOpen = openGroups.includes(group.id);
          const groupChecked = group.items.filter(label => getItem(group.id, label).checked).length;

          return (
            <div key={group.id} className="rounded-xl border border-stone-200/80 bg-white" data-testid={`photo-group-${group.id}`}>
              <button onClick={() => toggle(group.id)} className="flex items-center w-full px-4 py-2.5 text-left gap-2">
                <Camera className="w-3.5 h-3.5 text-stone-400" />
                <span className="flex-1 text-[11px] font-sans font-semibold text-stone-700">{group.label}</span>
                <span className="text-[9px] font-sans text-stone-400">{groupChecked}/{group.items.length}</span>
                <ChevronDown className={`w-3 h-3 text-stone-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
              </button>

              {isOpen && (
                <div className="px-4 pb-3 border-t border-stone-100 pt-2 space-y-1.5">
                  {group.items.map(label => {
                    const item = getItem(group.id, label);
                    const displayLabel = sub(label);
                    return <PhotoItem key={label} label={displayLabel} item={item}
                      onUpdate={(u) => updateItem(group.id, label, u)} guestNames={guestNames} />;
                  })}
                </div>
              )}
            </div>
          );
        })}

        {/* Custom photos */}
        <div className="rounded-xl border border-stone-200/80 bg-white">
          <div className="flex items-center justify-between px-4 py-2.5">
            <span className="text-[11px] font-sans font-semibold text-stone-700">Egne bilder</span>
            <button onClick={addCustom} data-testid="add-custom-photo"
              className="flex items-center gap-1 text-[10px] font-sans font-semibold text-primary hover:text-primary/80">
              <Plus className="w-3 h-3" /> Add
            </button>
          </div>
          {(data._custom || []).length > 0 && (
            <div className="px-4 pb-3 border-t border-stone-100 pt-2 space-y-1.5">
              {(data._custom || []).map((item, i) => (
                <div key={i} className="flex items-start gap-2">
                  <div className="flex-1">
                    <input value={item.label} onChange={e => updateCustom(i, { label: e.target.value })}
                      placeholder="Beskriv bildet..."
                      className="w-full text-[11px] font-sans font-medium px-2 py-1 border border-stone-200 rounded-lg focus:border-primary focus:outline-none mb-1" />
                    <PhotoItemFields item={item} onUpdate={(u) => updateCustom(i, u)} guestNames={guestNames} />
                  </div>
                  <button onClick={() => removeCustom(i)} className="p-1 text-stone-400 hover:text-red-500 mt-0.5">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const PhotoItem = ({ label, item, onUpdate, guestNames }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className={`rounded-lg border px-3 py-1.5 transition-all ${item.checked ? 'border-stone-200 bg-white' : 'border-stone-100 bg-stone-50 opacity-60'}`}>
      <div className="flex items-center gap-2">
        <button onClick={() => onUpdate({ checked: !item.checked })}
          className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-all flex-shrink-0 ${
            item.checked ? 'border-primary bg-primary' : 'border-stone-300'
          }`}>
          {item.checked && <Check className="w-2.5 h-2.5 text-white" />}
        </button>
        <span className="flex-1 text-[11px] font-sans text-stone-700 cursor-pointer" onClick={() => setExpanded(!expanded)}>{label}</span>
        {item.priority === 'Must have' && <Star className="w-3 h-3 text-amber-500 fill-amber-500" />}
        <button onClick={() => setExpanded(!expanded)} className="text-stone-400 hover:text-stone-600">
          <ChevronDown className={`w-3 h-3 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </button>
      </div>
      {expanded && <div className="mt-2 pt-2 border-t border-stone-100"><PhotoItemFields item={item} onUpdate={onUpdate} guestNames={guestNames} /></div>}
    </div>
  );
};

const PhotoItemFields = ({ item, onUpdate, guestNames }) => (
  <div className="grid grid-cols-2 gap-1.5">
    <div>
      <label className="text-[8px] font-sans font-bold text-stone-500 uppercase">Hvem</label>
      <input value={(item.people || []).join(', ')} onChange={e => onUpdate({ people: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
        placeholder="Names (comma-separated)" list="guest-names-list"
        className="w-full text-[10px] font-sans px-2 py-1 border border-stone-200 rounded focus:border-primary focus:outline-none" />
      <datalist id="guest-names-list">
        {guestNames.map(n => <option key={n} value={n} />)}
      </datalist>
    </div>
    <div>
      <label className="text-[8px] font-sans font-bold text-stone-500 uppercase">Lokasjon</label>
      <input value={item.location || ''} onChange={e => onUpdate({ location: e.target.value })}
        placeholder="Hvor" className="w-full text-[10px] font-sans px-2 py-1 border border-stone-200 rounded focus:border-primary focus:outline-none" />
    </div>
    <div>
      <label className="text-[8px] font-sans font-bold text-stone-500 uppercase">Time</label>
      <input value={item.time || ''} onChange={e => onUpdate({ time: e.target.value })}
        placeholder="Kl." className="w-full text-[10px] font-sans px-2 py-1 border border-stone-200 rounded focus:border-primary focus:outline-none" />
    </div>
    <div>
      <label className="text-[8px] font-sans font-bold text-stone-500 uppercase">Prioritet</label>
      <select value={item.priority || 'Must have'} onChange={e => onUpdate({ priority: e.target.value })}
        className="w-full text-[10px] font-sans px-2 py-1 border border-stone-200 rounded focus:border-primary focus:outline-none bg-white">
        {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
      </select>
    </div>
    <div className="col-span-2">
      <label className="text-[8px] font-sans font-bold text-stone-500 uppercase">Notes til fotograf</label>
      <input value={item.notes || ''} onChange={e => onUpdate({ notes: e.target.value })}
        placeholder="Spesielle \u00f8nsker, vinkel, stemning..."
        className="w-full text-[10px] font-sans px-2 py-1 border border-stone-200 rounded focus:border-primary focus:outline-none" />
    </div>
  </div>
);
